import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BriefcaseIcon, CheckCircle2Icon, UserPlusIcon } from 'lucide-react';
import { Lead } from '../../types/pro';
import { kr } from '../../utils/pro';
export type WonChoice = 'customer-and-job' | 'customer-only' | 'stage-only';
interface LeadWonDialogProps {
  lead: Lead | null;
  onClose: () => void;
  onConfirm: (choice: WonChoice) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const options: {
  id: WonChoice;
  icon: typeof BriefcaseIcon;
  title: string;
  description: string;
  primary?: boolean;
}[] = [{
  id: 'customer-and-job',
  icon: BriefcaseIcon,
  title: 'Opret kunde og opgave',
  description: 'Leadet bliver til en kunde, og der oprettes en opgave klar til planlægning.',
  primary: true
}, {
  id: 'customer-only',
  icon: UserPlusIcon,
  title: 'Kun opret kunde',
  description: 'Kunden oprettes, men opgaven laver du selv senere.'
}, {
  id: 'stage-only',
  icon: CheckCircle2Icon,
  title: 'Flyt kun kortet',
  description: 'Marker som vundet uden at oprette noget.'
}];

/** Shown when a lead is won — this is where sales hands over to execution. */
export function LeadWonDialog({
  lead,
  onClose,
  onConfirm
}: LeadWonDialogProps) {
  return <AnimatePresence data-id="element-432" data-mp="tep78z9dt34c">
      {lead && <div role="dialog" aria-modal="true" aria-labelledby="won-title" className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-433" data-mp="tfihia7juar8">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} onClick={onClose} className="absolute inset-0 bg-stone-900/50" data-id="element-434" data-mp="13bbrpmxsps5u" />

          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="relative w-full overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-md sm:rounded-2xl" data-id="element-435" data-mp="3a4nyfhfuuowq">
            <div className="px-5 py-5" data-id="element-436" data-mp="ubnmzcvtp4x2">
              <h2 id="won-title" className="text-lg font-bold tracking-tight text-stone-900" data-id="element-437" data-mp="ucp8ycednbtk">
                Konvertér lead til kunde og opgave?
              </h2>
              <p className="mt-1 text-sm text-stone-500" data-id="element-438" data-mp="147h4zbsb9qm8">
                {lead.name} · {lead.jobType} · {kr(lead.estimatedValue)}
              </p>

              <div className="mt-5 space-y-2" data-id="element-439" data-mp="1dzn18xpliu78">
                {options.map(option => {
              const Icon = option.icon;
              return <button key={option.id} onClick={() => onConfirm(option.id)} className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${option.primary ? 'border-brand-300 bg-brand-50 hover:bg-brand-100' : 'border-stone-200 hover:bg-stone-50'}`} data-id="element-440" data-mp="192efkn58o818">
                      <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${option.primary ? 'text-brand-600' : 'text-stone-400'}`} data-id="element-441" data-mp="1slkbe6gug5vs" />
                      <span className="min-w-0" data-id="element-442" data-mp="1iu7m9mmfjttg">
                        <span className="block text-sm font-semibold text-stone-900" data-id="element-443" data-mp="1nnithov5mclw">
                          {option.title}
                        </span>
                        <span className="mt-0.5 block text-xs text-stone-500" data-id="element-444" data-mp="1dwxo9ikz69u0">
                          {option.description}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </div>

            <div className="border-t border-stone-200 px-5 py-3" data-id="element-445" data-mp="14y2ejwiq2hly">
              <button onClick={onClose} className="w-full rounded-lg px-4 py-2 text-sm font-medium text-stone-500 transition-colors hover:bg-stone-100" data-id="element-446" data-mp="wv1wxqjwdjhj">
                Annuller
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}